import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import clsx from 'clsx';
const sizeClass = {
    sm: 'px-3.5 py-1.5 text-[12.5px]',
    md: 'px-[18px] py-2.5 text-[14px]',
    lg: 'px-6 py-3 text-[15px]',
};
/**
 * Pill-shaped button primitive in the Chaosology house style.
 *
 * - `primary`   — royal gradient fill (`--royal-hi` → `--royal`), white text,
 *                 inset top highlight + royal drop shadow. Use for the single
 *                 most important CTA on a screen.
 * - `secondary` — white glass (`--glass-bg-strong`) with ink-primary text and
 *                 the shared glass border. Default choice for toolbar buttons.
 * - `ghost`     — transparent body with the line-weight border; collapses to
 *                 `secondary` on hover.
 *
 * Uses `clsx` for conditional classes and a small amount of inline style for
 * the gradient fill / custom shadows that Tailwind can't express cleanly.
 */
export const Button = forwardRef(({ variant = 'secondary', size = 'md', leftIcon, rightIcon, isLoading = false, disabled, className, children, style, ...rest }, ref) => {
    const isPrimary = variant === 'primary';
    const isGhost = variant === 'ghost';
    return (_jsxs("button", { ref: ref, type: rest.type ?? 'button', disabled: disabled || isLoading, "aria-busy": isLoading || undefined, className: clsx(
        // Shared structure: pill, flex, font, transitions.
        'relative inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-[-0.005em]', "border transition-[transform,box-shadow,border-color,background-color] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]", 'cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0', 'focus-visible:outline-none focus-visible:shadow-[var(--shadow-ring)]', 
        // Variant surfaces.
        !isPrimary &&
            !isGhost &&
            // secondary — white glass
            'bg-[var(--glass-bg-strong)] text-[var(--ink-900)] border-[var(--glass-border)] shadow-[inset_0_1px_0_rgba(255,255,255,0.6),var(--shadow-xs)] backdrop-blur-[14px] hover:-translate-y-0.5 hover:shadow-[var(--shadow-sm)] hover:border-[rgba(91,63,228,0.3)]', isGhost &&
            'bg-transparent text-[var(--ink-800)] border-[var(--line)] shadow-none hover:bg-[var(--glass-bg-strong)] hover:border-[rgba(91,63,228,0.3)] hover:-translate-y-0.5', isPrimary &&
            'text-white border-white/20 hover:-translate-y-0.5', sizeClass[size], className), style: {
            ...(isPrimary
                ? {
                    background: 'linear-gradient(180deg, var(--royal-hi), var(--royal))',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 10px 24px -8px rgba(91,63,228,0.55)',
                }
                : {}),
            ...style,
        }, ...rest, children: [isLoading ? _jsx(Spinner, {}) : leftIcon, _jsx("span", { className: clsx(isLoading && 'opacity-70'), children: children }), !isLoading && rightIcon] }));
});
Button.displayName = 'Button';
/**
 * Inline loading spinner — uses `currentColor` so it matches whichever
 * variant it's rendered into.
 */
function Spinner() {
    return (_jsxs("svg", { className: "h-4 w-4 animate-spin", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("circle", { cx: "12", cy: "12", r: "9", stroke: "currentColor", strokeOpacity: "0.25", strokeWidth: "2.5" }), _jsx("path", { d: "M21 12a9 9 0 0 0-9-9", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round" })] }));
}
